import {Box, Button} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import React, {useEffect, useRef} from 'react';
import PlayerSessionShiftCard from './PlayerSessionShiftCard';
import {PlayerSessionShift} from '../../interfaces';
import {isNullOrUndefined} from '../../helpers';

interface SliderItem {
  id: number | string;
  disabled?: boolean;
}

interface Props {
  sessionType?: string;
  shiftClips: (PlayerSessionShift & {disabled?: boolean})[];
  items?: SliderItem[];
  videoIndex: number;
  setVideoIndex: (index: number) => void;
  videoRef: React.RefObject<HTMLVideoElement>;
  solidButtons?: boolean;
  renderCard?: (args: {
    item: SliderItem;
    index: number;
    active: boolean;
    onClick: () => void;
  }) => React.ReactNode;
}

const PlayerSessionShiftSlider: React.FC<Props> = ({
  sessionType,
  shiftClips = [],
  items,
  videoIndex,
  setVideoIndex,
  videoRef,
  solidButtons,
  renderCard,
}) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timer | null>(null);
  const sliderItems = (items ?? shiftClips) as SliderItem[];

  // Scroll the active card into view when videoIndex changes
  useEffect(() => {
    const container = sliderRef.current;
    if (!container) return;
    const child = container.querySelector(
      `[data-index="${videoIndex}"]`,
    ) as HTMLElement;
    if (child) {
      child.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [videoIndex]);

  const startScrollLeft = () => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(() => {
      const currentScrollLeftPosition = sliderRef.current?.scrollLeft;
      sliderRef.current?.scrollTo((currentScrollLeftPosition ?? 0) - 5, 0);
    }, 10);
  };

  const stopScrollLeft = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const startScrollRight = () => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(() => {
      const currentScrollLeftPosition = sliderRef.current?.scrollLeft;
      sliderRef.current?.scrollTo((currentScrollLeftPosition ?? 0) + 5, 0);
    }, 10);
  };

  const stopScrollRight = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
        }}>
        <Button
          variant={solidButtons ? 'contained' : 'outlined'}
          sx={{mr: 1, display: {xs: 'none', md: 'flex'}}}
          onMouseEnter={startScrollLeft}
          onMouseLeave={stopScrollLeft}
          onTouchStart={startScrollLeft}
          onTouchEnd={stopScrollLeft}>
          <ArrowBackIosNewIcon />
        </Button>
        <Box
          ref={sliderRef}
          sx={{
            flex: 1,
            display: 'flex',
            overflow: 'auto',
            scrollbar: 'none', // chrome
            overflowStyle: 'none', // IE and Edge
            scrollbarWidth: 'none', // Firefox
          }}>
          <Box
            sx={{
              display: 'flex',
            }}>
            {sliderItems.map((item, index: number) => (
              <div key={item.id} data-index={index}>
                {renderCard ? (
                  renderCard({
                    item,
                    index,
                    active: index === videoIndex,
                    onClick: () => {
                      setVideoIndex(index);
                      videoRef.current?.load();
                    },
                  })
                ) : (
                  <PlayerSessionShiftCard
                    sessionType={sessionType}
                    sx={{width: 250, mx: 1}}
                    shiftNumber={index + 1}
                    active={index === videoIndex}
                    disabled={item.disabled}
                    localDisplayStartTime={
                      (item as PlayerSessionShift).localDisplayStartTime
                    }
                    startTime={(item as PlayerSessionShift).startTime}
                    drill={(item as PlayerSessionShift).drill}
                    activeTime={
                      !isNullOrUndefined((item as PlayerSessionShift).end) &&
                      !isNullOrUndefined((item as PlayerSessionShift).start)
                        ? ((item as PlayerSessionShift).end as number) -
                          ((item as PlayerSessionShift).start as number)
                        : undefined
                    }
                    energyRatio={(item as PlayerSessionShift).energyRatio}
                    strideCountTotal={
                      (item as PlayerSessionShift).strideCountTotal
                    }
                    onClick={() => {
                      setVideoIndex(index);
                      videoRef.current?.load();
                    }}
                  />
                )}
              </div>
            ))}
          </Box>
        </Box>
        <Button
          variant={solidButtons ? 'contained' : 'outlined'}
          sx={{ml: 1, display: {xs: 'none', md: 'flex'}}}
          onMouseEnter={startScrollRight}
          onMouseLeave={stopScrollRight}
          onTouchStart={startScrollRight}
          onTouchEnd={stopScrollRight}>
          <ArrowForwardIosIcon />
        </Button>
      </Box>
      <Box sx={{display: {xs: 'flex', md: 'none'}, mt: 1}}>
        <Button
          variant={solidButtons ? 'contained' : 'outlined'}
          sx={{mr: 1, flexGrow: 1}}
          onTouchStart={startScrollLeft}
          onTouchEnd={stopScrollLeft}>
          <ArrowBackIosNewIcon />
        </Button>
        <Button
          variant={solidButtons ? 'contained' : 'outlined'}
          sx={{ml: 1, flexGrow: 1}}
          onTouchStart={startScrollRight}
          onTouchEnd={stopScrollRight}>
          <ArrowForwardIosIcon />
        </Button>
      </Box>
    </Box>
  );
};

export default React.memo(PlayerSessionShiftSlider);
